import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Fade } from '@column/column-ui-kit';

import { LogoLoading } from '~/elements/LogoLoading';

interface Props {
  centerSpinner?: boolean;
  fullHeight?: boolean;
  minHeight?: boolean;
  isLoading: boolean;
}

const Container = styled.div<{ $minHeight?: boolean }>`
  overflow: visible;
  transition: height 300ms ease;
  min-height: ${({ $minHeight }) => ($minHeight ? '150px' : 'auto')};
`;

const LoadingWrapper = styled.div<{ fullHeight?: boolean; $centerSpinner?: boolean; $minHeight?: boolean }>`
  display: flex;
  min-height: 150px;

  ${({ fullHeight }) =>
    fullHeight
      ? css`
          margin-top: 10%;
        `
      : css`
          margin-top: 30px;
        `};

  ${({ $centerSpinner }) =>
    $centerSpinner &&
    css`
      align-items: center;
      justify-content: center;
      margin-top: 0;
      position: relative;
    `}
`;

const LoadingLogo = styled(LogoLoading)<{ $centerSpinner?: boolean }>`
  ${({ $centerSpinner }) =>
    $centerSpinner &&
    css`
      left: auto;
      margin: 0;
      position: relative;
      top: auto;
    `}
`;

export const Loading: React.FC<React.PropsWithChildren<Props>> = ({
  centerSpinner,
  children,
  minHeight = true,
  fullHeight,
  isLoading,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const childrenRef = useRef<HTMLDivElement>(null);

  const [isChildrenVisible, setChildrenVisible] = useState(!isLoading);
  useEffect(() => {
    if (isLoading) {
      setChildrenVisible(false);
      const timeoutId = setTimeout(() => setSpinnerVisible(true), 200);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);

  const [isSpinnerVisible, setSpinnerVisible] = useState(isLoading);
  useEffect(() => {
    if (!isLoading) {
      setSpinnerVisible(false);
      setChildrenVisible(true);
    }
  }, [isLoading]);

  const updateContainerHeight = useCallback(() => {
    const spinnerHeight = spinnerRef.current?.offsetHeight || 0;
    const childrenHeight = childrenRef.current?.offsetHeight || 0;
    const newHeight = isSpinnerVisible ? spinnerHeight : childrenHeight;

    if (containerRef.current && newHeight !== 0) {
      containerRef.current.style.height = `${newHeight}px`;
    }
  }, [isSpinnerVisible]);

  useLayoutEffect(() => {
    updateContainerHeight();
  }, [isChildrenVisible, isSpinnerVisible, updateContainerHeight]);

  useEffect(() => {
    if (!childrenRef.current) return;
    const observer = new window.ResizeObserver(updateContainerHeight);
    observer.observe(childrenRef.current);
    return () => observer.disconnect();
  }, [updateContainerHeight]);

  return (
    <Container ref={containerRef} $minHeight={minHeight}>
      <Fade show={isSpinnerVisible} timeout={200} onEnter={updateContainerHeight}>
        <LoadingWrapper fullHeight={fullHeight} $centerSpinner={centerSpinner} $minHeight={minHeight} ref={spinnerRef}>
          <LoadingLogo $centerSpinner={centerSpinner} />
        </LoadingWrapper>
      </Fade>
      {children && (
        <Fade show={isChildrenVisible} timeout={200} onEnter={updateContainerHeight}>
          <div ref={childrenRef}>{children}</div>
        </Fade>
      )}
    </Container>
  );
};
